import { api } from "../api";

// -----------------------------------------------------------------------
// Типы. Возвращаются бэкендом в snake_case; здесь их не преобразуем.
// -----------------------------------------------------------------------

export interface TierLinePercent {
  line: number;
  deposit: number;
  cardIssue: number;
  transfer: number;
}

export interface Tier {
  code: string;
  name: string;
  min_referrals: number;
  line_percents: TierLinePercent[];
  is_premium: boolean;
  premium_price_usd: string | null;
  sort_order: number;
  is_active: boolean;
  theme: string;
}

export interface Account {
  user_id: string;
  tier_code: string;
  balance: string;
  total_earned: string;
  total_withdrawn: string;
  is_premium: boolean;
  premium_since: string | null;
  premium_paid_usd: string | null;
  updated_at: string;
}

export interface ReferralOverview {
  account: Account;
  tier: Tier;
  next_tier: Tier | null;
  progress: number;
  referrals_total: number;
  referrals_active: number;
  referral_code: string;
  referral_link: string;
  tiers: Tier[];
  settings: {
    min_payout_usdt: string;
    payout_networks: string[];
  };
}

export type ReferralSourceType =
  | "DEPOSIT"
  | "CARD_ISSUE"
  | "TRANSFER"
  | "EXCHANGE"
  | "SBP_PAYOUT"
  | "ADJUSTMENT";

export interface Accrual {
  id: string;
  user_id: string;
  from_user_id: string;
  from_user_name: string | null;
  source_type: ReferralSourceType;
  source_id: string | null;
  line_number: number;
  tier_code: string;
  bonus_percent: string;
  base_amount: string;
  base_currency: string;
  amount_usdt: string;
  created_at: string;
}

export type ReferralPayoutStatus =
  | "PENDING"
  | "APPROVED"
  | "PROCESSING"
  | "SUCCEEDED"
  | "REJECTED"
  | "FAILED";

export interface Payout {
  id: string;
  user_id: string;
  amount: string;
  network_code: string;
  address: string;
  memo: string | null;
  status: ReferralPayoutStatus;
  linked_order_id: string | null;
  admin_comment: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
}

// -----------------------------------------------------------------------
// API-функции. Пути соответствуют backend `MeReferralsController`.
// -----------------------------------------------------------------------

export async function getReferralOverview(): Promise<ReferralOverview> {
  const { data } = await api.get<ReferralOverview>("/me/referrals/overview");
  return data;
}

export async function getReferralAccruals(
  page = 1,
  pageSize = 30,
): Promise<PaginatedResult<Accrual>> {
  const { data } = await api.get<PaginatedResult<Accrual>>(
    "/me/referrals/accruals",
    { params: { page, pageSize } },
  );
  return data;
}

export async function getReferralPayouts(
  page = 1,
  pageSize = 30,
): Promise<PaginatedResult<Payout>> {
  const { data } = await api.get<PaginatedResult<Payout>>(
    "/me/referrals/payouts",
    { params: { page, pageSize } },
  );
  return data;
}

export interface CreatePayoutPayload {
  amount: string;
  networkCode: string;
  address: string;
  memo?: string;
}

export async function createReferralPayout(
  payload: CreatePayoutPayload,
): Promise<Payout> {
  const { data } = await api.post<Payout>("/me/referrals/payouts", payload);
  return data;
}

export async function purchasePremium(): Promise<Account> {
  const { data } = await api.post<Account>(
    "/me/referrals/premium/purchase",
    {},
  );
  return data;
}

export async function bindReferrer(code: string): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>(
    "/me/referrals/bind-referrer",
    { code },
  );
  return data;
}
